import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useSelector, useDispatch, shallowEqual } from "react-redux";
// Google Maps api for react
import { GoogleMap, useJsApiLoader } from "@react-google-maps/api";

// extra components
import SelectedUnitMarkers from "../../components/SelectedUnitMarkers/SelectedUnitMarkers";
import UnitsMarkers from "../../components/UnitsMarkers/UnitsMarkers";
import MapLateralMenu from "../../components/MapLateralMenu";
import Select from "react-select";
import { LateralCardUnits } from "components/LateralCardUnits/LateralCardUnits";
import makeAnimated from "react-select/animated";
import CoveragePolygons from "../../components/CoveragePolygons/CoveragePolygons";

// material-ui components
import Snackbars from "components/Snackbar/Snackbar.js";
import Button from "components/CustomButtons/Button.js";
import ArrowBack from "@mui/icons-material/ArrowBack";

// styles
import "./mapPages.css";
import { PageContainer, MapContainer } from "./styles";

// Hooks
import { useColorTheme } from "hooks/useColorTheme";
import { useHistory } from "react-router-dom";

// actions creators
import {
  clearUnits,
  clearError,
  cancel,
  toggleIsNote,
  removeTableDetails,
} from "redux/ducks/mapPage";
import { CancelModal } from "components/CancelModal/CancelModal";
// helpers
import { modals as modalsDictionary } from "helpers/dictionary";
import { LoadingComponent } from "components/Loading/LoadingComponent";

const animatedComponents = makeAnimated();

const DEFAULT_CENTER = {
  lat: parseFloat(process.env.REACT_APP_DEFAULT_LAT) || 20.6597,
  lng: parseFloat(process.env.REACT_APP_DEFAULT_LNG) || -103.3496,
};

const causesStation = (causes, item) => {
  if (item && causes[item.idStation]) {
    return causes[item.idStation].map((cause) => {
      return {
        value: cause.id,
        label: `${cause.type} - ${cause.name}`,
      };
    });
  }
  return [];
};

export default function MapPage() {
  const dispatch = useDispatch();
  let history = useHistory();

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_MAPS_API_KEY,
  });
  // selectors
  const colorBg = useSelector((state) => state.config.colorBg);
  const causesMap = useSelector((state) => state.map.causesMap, shallowEqual);
  const { units, isOpenDetails, tableDetails } = useSelector(
    (state) => state.map,
    shallowEqual
  );
  const itemDetail = useSelector((state) => state.map.itemDetail);
  const isNote = useSelector((state) => state.map.isNote, shallowEqual);
  const isLoadingDetails = useSelector(
    (state) => state.map.isLoadingDetails,
    shallowEqual
  );
  const { error, errorMessage } = useSelector(
    (state) => state.map,
    shallowEqual
  );

  // end
  const [currentPosition, setCurrentPosition] = useState(DEFAULT_CENTER);
  const [modals, setModals] = React.useState({ cancel: false });
  const [itemModal, setItemModal] = useState(null);
  const [cancelCause, setCancelCause] = useState(null);

  // Custom hooks
  const { color, colorRGB } = useColorTheme(colorBg);

  // Callbacks
  const clear = useCallback(() => {
    dispatch(clearUnits());
  }, [dispatch]);
  const clearSnackbar = useCallback(() => {
    dispatch(clearError());
  }, [dispatch]);
  const removeDetails = useCallback(() => {
    dispatch(removeTableDetails());
  }, [dispatch]);

  const toggle = useCallback(() => {
    dispatch(toggleIsNote());
  }, [dispatch]);

  // handlers

  const handleOpenModals = useCallback((item, modal) => {
    setItemModal(item);
    setModals((prevState) => {
      return { ...prevState, [modal]: true };
    });
  }, []);
  const handleCloseModals = useCallback((modal) => {
    setModals((prevState) => {
      return { ...prevState, [modal]: false };
    });
    setCancelCause(null);
  }, []);
  const handleCancel = useCallback(() => {
    const body = { cause: cancelCause ? cancelCause.value : "" };
    const idToCancel = itemModal ? itemModal.id : "";
    const idUnit = itemDetail ? itemDetail.id : "";
    dispatch(cancel(idUnit, idToCancel, body));
    handleCloseModals(modalsDictionary.cancel);
  }, [cancelCause, itemModal, itemDetail, dispatch, handleCloseModals]);
  const handleGoBack = useCallback(() => {
    history.goBack();
  }, [history]);

  const causesMemoized = useMemo(() => causesStation(causesMap, itemDetail), [
    causesMap,
    itemDetail,
  ]);

  const success = (position) => {
    const currentPosition = {
      lat: parseFloat(position.coords.latitude.toFixed(4)),
      lng: parseFloat(position.coords.longitude.toFixed(4)),
    };
    setCurrentPosition(currentPosition);
  };

  // Effects
  useEffect(() => {
    clear();
  }, [clear]);

  useEffect(() => {
    navigator.geolocation.getCurrentPosition(success, () => {
      // Geolocation not available or denied — using default center
    });
  }, []);

  const markersOnMap = () => {
    if (isOpenDetails) {
      const unitID = tableDetails[0];
      const unit = units.find((item) => item.number === unitID);

      return (
        unit && (
          <React.Fragment key="selected-markers">
            <SelectedUnitMarkers
              unit={unit}
              isLoading={unit.isLoading}
              color={colorRGB}
            />
            <CoveragePolygons selectedUnit={unit} />
          </React.Fragment>
        )
      );
    } else {
      return <UnitsMarkers key="all-markers" color={colorRGB} />;
    }
  };

  return (
    <PageContainer>
      <LateralCardUnits></LateralCardUnits>

      <MapContainer>
        {isLoaded ? (
          <GoogleMap
            mapContainerClassName="mapPage_map"
            zoom={13}
            center={currentPosition}
            options={{
              fullscreenControl: false,
            }}
          >
            {markersOnMap()}
          </GoogleMap>
        ) : (
          <LoadingComponent label="Cargando mapa..." height="100%" />
        )}
      </MapContainer>
      <Snackbars
        place="tr"
        color="danger"
        message={errorMessage}
        open={error}
        closeNotification={() => clearSnackbar()}
        close
      />
      <CancelModal
        colorRGB={colorRGB}
        itemModal={itemModal}
        modal={modals.cancel}
        isLoading={false}
        title={`¿Cancelar pedido con folio ${
          itemModal ? itemModal.folio : ""
        }?`}
        message="El pedido se cancelara."
        onCloseModal={handleCloseModals}
        onCancel={handleCancel}
      >
        <br />
        <Select
          placeholder="Causas..."
          components={animatedComponents}
          className="list-select__container"
          options={causesMemoized}
          value={cancelCause}
          onChange={(event) => setCancelCause(event)}
        />
      </CancelModal>
      <MapLateralMenu
        isOpen={isOpenDetails}
        item={itemDetail}
        isLoading={isLoadingDetails}
        colorRGB={colorRGB}
        color={color}
        isNote={isNote}
        onChangeDoctos={toggle}
        onClose={removeDetails}
        onOpenCancelModal={handleOpenModals}
      />
      <Button
        onClick={handleGoBack}
        justIcon
        round
        color="danger"
        style={{ position: "absolute", top: "15px", right: "20px" }}
      >
        <ArrowBack />
      </Button>
    </PageContainer>
  );
}
